import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { PrimaryButton } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { HeadingLevel } from '@/components/common/MarkUp';
import { TabView } from '@/components/common/TabView';
import { Tag } from '@/components/common/Tag';
import { Section } from '@/components/layout/Section';
import { BlogCards } from '@/components/page/blog/generateBlogCards';

import type {
  SanityBlogAuthorType,
  SanityBlogCategoryType,
  SanityLatestPostsType,
} from '@/types/sanity';
import type { BlogPostPreview } from '@/types/shared';

export interface PostsByCategoriesProp {
  blogPosts: Array<BlogPostPreview>;
  _id: string;
}

export interface LatestPostsDataProps {
  categories: Array<SanityBlogCategoryType>;
  postsPerCategory: Array<PostsByCategoriesProp>;
  allAuthors: Array<SanityBlogAuthorType>;
  latestPosts: Array<BlogPostPreview>;
}

export interface LatestPostsProps extends SanityLatestPostsType {
  latestPostsData: LatestPostsDataProps;
}

export const LatestPosts = ({
  tag,
  caption,
  heading,
  latestPostsData,
}: LatestPostsProps) => {
  const { allAuthors, categories, postsPerCategory, latestPosts } =
    latestPostsData;

  const categoriesWithAll = [
    {
      name: 'All',
      slug: { current: '', _type: 'slug' },
      _id: 'blogCategory-all',
    },
    ...categories,
  ];

  const allPostsByCategory = [
    { blogPosts: latestPosts, _id: 'blogCategory-all' },
    ...postsPerCategory,
  ];

  const tabData = categoriesWithAll.map((tab) => {
    return {
      label: tab.name,
      variant: 'standard',
      content: <TabContent tab={tab} posts={allPostsByCategory} />,
    };
  });

  return (
    <Section backgroundColour="--background-subtle" verticalPadding>
      <HeadingLevel>
        <HeadingSection>
          {tag?.tagText && (
            <Tag
              bgColour={tag?.tagColour}
              margin="0 0 var(--spacing-xxx-small) 0"
            >
              {tag.tagText}
            </Tag>
          )}
          <CaptionHeadingDescription
            captionHeadingDescription={{ caption, heading }}
            padding={{ description: 200 }}
            desktopAlignment="center"
            mobileAlignment="center"
          />
        </HeadingSection>
        <TabViewWrapper tabs={tabData} allAuthors={allAuthors} />
      </HeadingLevel>
    </Section>
  );
};

const HeadingSection = styled.div``;

const TabViewWrapper = styled(TabView)`
  ${screen.sm} {
    align-items: center;
  }
`;

export const TabContent = ({ tab, posts }) => {
  const categoryPosts = posts.filter((cat) => cat._id === tab._id)[0].blogPosts;

  return (
    <BlogSectionWrapper>
      <BlogCardsWrapper>
        {!!categoryPosts &&
          categoryPosts.map((post) => (
            <BlogCards
              post={post}
              key={post._id}
              fallbackImage={post.featuredImage?.asset}
              fallbackAuthorImage={post}
            />
          ))}
      </BlogCardsWrapper>
      <StyledButton
        key={tab._id}
        className="tracking-latest-posts__cta"
        iconPosition="right"
        href={
          tab.name === 'All' ? '/blog' : `/blog/category/${tab.slug.current}`
        }
        componentTheme="light"
        aria-label={`Read more blogs in the ${tab.name} category `}
      >
        Read more
      </StyledButton>
    </BlogSectionWrapper>
  );
};

const BlogSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledButton = styled(PrimaryButton)`
  margin-top: var(--spacing-300);
`;

const BlogCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  position: relative;
  z-index: 1;

  article {
    margin-bottom: 0;
    margin-right: 0 !important;
    background-color: var(--surface-warm-base);
  }

  article:nth-of-type(3) {
    display: none;
  }

  ${screen.sm} {
    flex-direction: row;
  }

  ${screen.md} {
    gap: var(--spacing-300);

    article:nth-of-type(3) {
      display: block;
    }
  }
`;
